import classnames from 'classnames'
import PropTypes from 'prop-types'
import * as uuid from 'uuid'

import {connect} from 'react-redux'
import {fromJS, Map, List} from 'immutable'
import {useMutation} from '@apollo/client'
import {useCallback, useEffect, useState, useRef} from 'react'
import {withRouter} from 'react-router'

import {ModalHeader} from '../../../../../shared_components/core/modals/ModalHeaders'
import Button from '../../../../../shared_components/mui_base_components/button/Button'
import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import Closer from '../../../../../shared_components/core/closer/Closer'
import Fields from '../../../../../shared_components/forms/v2/fields'
import Form from '../../../../../shared_components/forms/v2/Form'
import FormLevelErrors, {DEFAULT_FORM_LEVEL_ERROR} from '../../../../../shared_components/forms/v2/FormLevelErrors'
import Modals from '../../../../../shared_components/core/modals/Modals'
import multipleFormFieldWrapper from '../../../../../shared_components/forms/v2/MultipleFormFieldWrapper'

import {camelizeImmutableData} from '../../../../../lib/graphQLTools'
import {contactLabel, GENDER_OPTIONS} from '../../../../../lib/contactTools'
import {CONTACTS} from '../../../../../graphql/queries/userData'
import {CREATE_CONTACT, UPDATE_CONTACT} from '../../../../../graphql/mutations/userData'
import {currentUserConfig} from '../../../../../lib/userTools'
import {dateWithMaskFromForm} from '../../../../../lib/dateTools'
import {findSelectedValueId} from '../../../../../shared_components/forms/v2/lib/tools'
import Logger from '../../../../../lib/NewLogger'
import validators from '../../../../../shared_components/forms/v2/validators'

import './newContact.scss'

const multipleFields = Map({
  email: 'emails',
  address: 'addresses',
  'other-name': 'other-names',
  'phone-number': 'phone-numbers'
})

const Addresses = multipleFormFieldWrapper(Fields.Address)
const Emails = multipleFormFieldWrapper(Fields.Email)
const OtherNames = multipleFormFieldWrapper(Fields.Text)
const Phones = multipleFormFieldWrapper(Fields.Tel)

const formatContactHeading = selectedContact => {
  if (selectedContact.isEmpty())
    return 'New Contact'

  return contactLabel(selectedContact)
}

export const NewContact = props => {
  const formRef = useRef()
  const contactFormWrapperRef = useRef()
  const contactHeading = formatContactHeading(props.selectedContact)

  const [formLevelErrors, setFormLevelErrors] = useState(List())
  const [processing, setProcessing] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const updateScrollShadow = useCallback(() => {
    const formNode = contactFormWrapperRef.current?.querySelector('form.core')

    if (formNode)
      setCanScrollDown(formNode.scrollHeight - formNode.scrollTop - formNode.clientHeight > 1)
  }, [])

  useEffect(() => {
    const wrapperNode = contactFormWrapperRef.current

    if (!wrapperNode)
      return

    wrapperNode.addEventListener('scroll', updateScrollShadow, {capture: true, passive: true})

    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollShadow)
    resizeObserver?.observe(wrapperNode)

    return () => {
      wrapperNode.removeEventListener('scroll', updateScrollShadow, {capture: true})
      resizeObserver?.disconnect()
    }
  }, [updateScrollShadow])

  useEffect(updateScrollShadow, [updateScrollShadow])

  const [updateContact] = useMutation(UPDATE_CONTACT)

  const [createContact] = useMutation(CREATE_CONTACT, {
    update: (cache, {data}) => {
      const {contacts} = cache.readQuery({query: CONTACTS, variables: {everplanId: props.everplanId}})

      cache.writeQuery({
        query: CONTACTS,
        data: {contacts: [...contacts, data.createContact]},
        variables: {everplanId: props.everplanId}
      })
    }
  })

  const logEvent = event => {
    Logger.log({
      name: event,
      payload: {
        context: props.context,
        element_id: props.element.get('id'),
        item: props.itemName,
        type: props.eventType,
        view_id: props.view.get('id'),
        wildcard: props.itemName
      }
    })
  }

  const isMissingMandatoryField = formData => (
    !formData.keySeq().some(key =>
      ['first-name', 'last-name', 'company-name'].includes(key)
    )
  )

  const invalidFormError = formData => {
    const errors = []

    if (formRef.current) {
      if (isMissingMandatoryField(formData))
        errors.push('Please add either name or company.')

      if (!formRef.current.errors('birthdate').isEmpty())
        errors.push('Please add a valid date.')
    }

    return List(errors)
  }

  const birthdateDataFromForm = formData => {
    const birthdate = formData.get('birthdate', Map())

    if (birthdate.isEmpty()) {
      return formData.delete('birthdate')
    } else {
      const dateWithMask = birthdate.get('date') ? birthdate : dateWithMaskFromForm(birthdate)

      return formData
        .set('birthdate', dateWithMask.get('date'))
        .set('birthdate-mask', dateWithMask.get('mask'))
    }
  }

  const cleanAddress = formData => {
    const addressList = formData.get('addresses')

    if (addressList)
      return formData.set('addresses', addressList.map(address => address.delete('typename')))
    else
      return formData
  }

  const createOrUpdateContact = variables => {
    if (props.selectedContact.get('id')) {
      logEvent('edited_contact')
      updateContact({
        variables,
        onCompleted: returnedData => {
          setProcessing(false)
          props.hideContactModal(returnedData.updateContact.id)
        }
      })
    } else {
      logEvent('created_new_contact')
      createContact({
        variables,
        onCompleted: returnedData => {
          setProcessing(false)
          props.hideContactModal(returnedData.createContact.id)
        }
      })
    }
  }


  /** Method to flatten a contact's name to what the api expects
   * @param {Map} contact A Map that contains contact to be created or updated
   * @return {Map} A Map that has the name flattened out
  */
  const flattenName = contact => contact.merge(contact.get('name')).delete('name')

  const constructName = data => Map({
    'first-name': data.get('first-name'),
    'middle-name': data.get('middle-name'),
    'last-name': data.get('last-name')
  })

  const constructBirthdate = data => {
    if (data.get('birthdate'))
      return {date: data.get('birthdate'), mask: data.get('birthdate-mask')}
  }

  const clearFormErrors = () => setFormLevelErrors(errors => (errors.isEmpty() ? errors : List()))

  const cancel = () => {
    logEvent('closed_add_new_contact')
    props.hideContactModal()
  }

  const formDataPayload = () => {
    let formData = formRef.current.formData().map(field => field.get('value'))

    formData = flattenName(formData).filter(value => value)

    formData = birthdateDataFromForm(formData)

    const gender = findSelectedValueId({selectId: 'gender', form: formRef.current})

    if (gender)
      formData = formData.set('gender', gender)


    multipleFields.forEach((fieldValue, field) => {
      const fields = formData.filter((value, key) => key.startsWith(field))
      formData = fields.reduce((data, value, key) => data.delete(key), formData)

      if (!fields.isEmpty())
        formData = formData.set(fieldValue, fields.toList())
    })

    return formData.set('id', props.selectedContact.isEmpty() ? uuid.v4() : props.selectedContact.get('id'))
  }

  const submit = () => {
    formRef.current.onSubmit()
    let formData = formDataPayload()

    if (formRef.current.isValid() && !isMissingMandatoryField(formData)) {
      formData = cleanAddress(formData)

      const variables = {
        input: camelizeImmutableData(formData),
        everplanId: props.everplanId
      }
      setProcessing(true)
      createOrUpdateContact(variables)
    } else {
      const errors = invalidFormError(formData)

      setFormLevelErrors(errors.isEmpty() ? List([DEFAULT_FORM_LEVEL_ERROR]) : errors)
    }
  }

  const contactData = (formRef.current && formDataPayload()) || props.selectedContact || Map()

  const genderValue = contactData.get('gender') ?
    GENDER_OPTIONS.find(
      genderOption => genderOption.get('id') === contactData.get('gender')
    )?.get('value') || 'Other' :
    GENDER_OPTIONS.getIn([0, 'value'])

  return (
    <Modals.CardModal
      className='new-contact forms-playground'
      closerComponent={() => <Closer closer={cancel} />}
      showModal={true}>
      <ModalHeader heading={contactHeading} />
      <div
        className={classnames('contact-form-wrapper', {'can-scroll-down': canScrollDown})}
        ref={contactFormWrapperRef}>
        <Form
          className='core new-contact-form'
          hideFormLevelErrors={true}
          onUpdate={clearFormErrors}
          ref={formRef}>
          <Fields.Name
            data={
              fromJS({
                id: 'name',
                legend: 'Name',
                name: 'name',
                value: constructName(contactData)
              })
            }
            showMiddleName={true}
          />
          <OtherNames
            legend='Other names used'
            name='other-name'
            values={contactData.get('other-names')}
          />
          <Emails
            name='email'
            legend='Email'
            values={contactData.get('emails')}
          />
          <Phones
            name='phone-number'
            legend='Phone'
            values={contactData.get('phone-numbers')}
          />
          <Fields.Text
            data={
              fromJS({
                id: 'company-name',
                legend: 'Company',
                name: 'company-name',
                value: contactData.get('company-name')
              })
            }
          />
          <Addresses
            className='address-field'
            name='address'
            legend='Address'
            values={contactData.get('addresses')}
          />
          <Fields.Text
            data={fromJS({
              id: 'website',
              legend: 'Website',
              name: 'website',
              value: contactData.get('website')
            })}
          />
          <Fields.Date
            data={
              fromJS({
                id: 'birthdate',
                legend: 'Birthdate',
                name: 'birthdate',
                value: constructBirthdate(contactData)
              })
            }
            validator={validators.responseDateValidator}
            className='birthdate'
          />
          <Fields.Select
            data={
              fromJS({
                id: 'gender',
                items: GENDER_OPTIONS,
                legend: 'Gender',
                name: 'gender',
                value: genderValue
              })
            }
          />
          <Fields.TextArea
            data={
              fromJS({
                id: 'notes',
                legend: 'Notes',
                name: 'notes',
                value: contactData.get('notes')
              })
            }
          />
        </Form>
        <FormLevelErrors errors={formLevelErrors} />
        <ButtonGroup>
          <Button className='cancel' disabled={processing} onClick={cancel}>Cancel</Button>
          <Button onClick={submit} loading={processing}>Save</Button>
        </ButtonGroup>
      </div>
    </Modals.CardModal>
  )
}

NewContact.defaultProps = {
  selectedContact: Map(),
  processing: false,
  view: Map(),
  element: Map()
}

NewContact.propTypes = {
  context: PropTypes.string,
  element: PropTypes.instanceOf(Map),
  eventType: PropTypes.string,
  everplanId: PropTypes.number,
  hideContactModal: PropTypes.func,
  itemName: PropTypes.string,
  processing: PropTypes.bool,
  selectedContact: PropTypes.instanceOf(Map),
  view: PropTypes.instanceOf(Map)
}

const mapStateToProps = ({api}, ownProps) => ({
  // If the route has an `everplanId` then do not assume that the plan being edited
  // is the current user's plan:
  everplanId: parseInt((ownProps.params && ownProps.params.everplanId) || currentUserConfig(api).get('everplan-id'), 10)
})

export default withRouter(connect(mapStateToProps)(NewContact))
